// Vistoria de abertura (Etapas 1 e 3 do módulo): contagem física de cada produto — tela
// pensada para o celular — e valorização inicial (valor unitário manual ou tirado de uma NF).
// Concluir gera os movimentos ABERTURA e abre o 1º período. Regras em
// backend/services/estoque/inventarios.py.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Check, CheckCircle2, FileText, RefreshCw } from "lucide-react";
import {
  getEstoqueAbertura, getRoleFromToken,
  postEstoqueAbertura, postEstoqueCancelarInventario, postEstoqueConcluirAbertura,
  putEstoqueValorAbertura,
} from "../../api";
import { permissoesEstoque } from "../../constants/estoque";
import { AbaContagem, BarraProgresso, Card, agruparPorCategoria } from "./ContagemInventario";
import EstoqueLayout from "./EstoqueLayout";
import {
  AvisoErro, Campo, Modal, NumeroInput, btnStyle, formatBRL, formatQtd, inputStyle, numeroParaInput,
  palette, parseNumero, useIsMobile,
} from "./ui";

const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const formatData = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");

// ─── Início ───────────────────────────────────────────────────────────────────

function IniciarVistoria({ C, perm, estoqueTemMovimentacao, onIniciada }) {
  const [data, setData] = useState(hojeISO());
  const [observacao, setObservacao] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  async function iniciar() {
    setErro("");
    setSalvando(true);
    try {
      await postEstoqueAbertura({ data, observacao: observacao.trim() || null });
      onIniciada();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card C={C} style={{ maxWidth: 560 }}>
      <h2 style={{ margin: "0 0 8px", fontSize: 18 }}>Vistoria inicial do estoque</h2>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
        A vistoria é o ponto de partida do controle: a equipe percorre o estoque e registra a quantidade de cada produto,
        na unidade padrão dele. Depois, o financeiro informa o valor unitário de cada item (manual ou pela NF mais recente)
        e conclui a abertura. Ela acontece uma única vez.
      </p>
      {estoqueTemMovimentacao ? (
        <AvisoErro C={C}>O estoque já tem movimentações — a vistoria de abertura não pode mais ser feita.</AvisoErro>
      ) : !perm.operador ? (
        <div style={{ fontSize: 13, color: C.muted }}>Seu perfil não pode iniciar a vistoria.</div>
      ) : (
        <>
          <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Campo label="Data da vistoria" C={C} obrigatorio ajuda="Data em que o estoque foi contado — é a data do saldo de abertura.">
              <input type="date" value={data} max={hojeISO()} onChange={(e) => setData(e.target.value)} style={inputStyle(C)} />
            </Campo>
            <Campo label="Observação" C={C}>
              <input value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Ex.: contagem feita por Fulano e Beltrano" style={inputStyle(C)} />
            </Campo>
            <button onClick={iniciar} disabled={salvando || !data} style={{ ...btnStyle(C.brand), opacity: salvando ? 0.6 : 1 }}>
              {salvando ? "Iniciando..." : "Iniciar vistoria"}
            </button>
          </div>
        </>
      )}
    </Card>
  );
}

// ─── Valorização ──────────────────────────────────────────────────────────────

function ModalValorNF({ C, item, onClose, onSalvar }) {
  const [form, setForm] = useState({ referencia_nf: item.referencia_nf || "", nf_quantidade: "", nf_fator: "1", nf_valor_total: "" });
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const set = (campo) => (v) => setForm((f) => ({ ...f, [campo]: v }));

  const q = Number(parseNumero(form.nf_quantidade));
  const f = Number(parseNumero(form.nf_fator));
  const v = Number(parseNumero(form.nf_valor_total));
  const convertida = q > 0 && f > 0 ? q * f : null;
  const custoPrevisto = convertida && form.nf_valor_total ? v / convertida : null;

  async function salvar() {
    const dados = {
      origem_custo: "NF",
      referencia_nf: form.referencia_nf.trim(),
      nf_quantidade: parseNumero(form.nf_quantidade),
      nf_fator: parseNumero(form.nf_fator),
      nf_valor_total: parseNumero(form.nf_valor_total),
    };
    if (!dados.referencia_nf || !dados.nf_quantidade || !dados.nf_fator || dados.nf_valor_total === null) {
      setErro("Preencha a NF de referência, a quantidade, o fator e o valor do item.");
      return;
    }
    setSalvando(true);
    setErro("");
    try {
      await onSalvar(dados);
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  }

  return (
    <Modal
      C={C}
      titulo={`Valor pela NF · ${item.produto_nome}`}
      onClose={onClose}
      largura={480}
      rodape={
        <>
          <button onClick={onClose} style={btnStyle(C.muted, true)}>Cancelar</button>
          <button onClick={salvar} disabled={salvando} style={{ ...btnStyle(C.brand), opacity: salvando ? 0.6 : 1 }}>{salvando ? "Salvando..." : "Usar este valor"}</button>
        </>
      }
    >
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
        Informe a linha do produto na nota fiscal mais recente. O sistema converte para a unidade padrão ({item.unidade_sigla})
        e calcula o valor unitário: valor do item ÷ (quantidade na NF × fator).
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Campo label="NF de referência" C={C} obrigatorio ajuda="Número da nota e fornecedor. Ex.: NF 4521 · Atacadão">
          <input value={form.referencia_nf} onChange={(e) => set("referencia_nf")(e.target.value)} style={inputStyle(C)} autoFocus />
        </Campo>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}>
          <Campo label="Qtd. na NF" C={C} obrigatorio><NumeroInput C={C} value={form.nf_quantidade} onChange={set("nf_quantidade")} /></Campo>
          <Campo label={`Fator (${item.unidade_sigla} por embalagem)`} C={C} obrigatorio><NumeroInput C={C} value={form.nf_fator} onChange={set("nf_fator")} casas={6} /></Campo>
          <Campo label="Valor total do item (R$)" C={C} obrigatorio><NumeroInput C={C} value={form.nf_valor_total} onChange={set("nf_valor_total")} casas={2} /></Campo>
        </div>
        <div style={{ background: C.bgActive, borderRadius: 8, padding: 12, fontSize: 13 }}>
          {convertida ? `${formatQtd(convertida)} ${item.unidade_sigla}` : "—"} ·{" "}
          <strong>{custoPrevisto !== null ? `${formatBRL(custoPrevisto)} por ${item.unidade_sigla}` : "valor unitário"}</strong>
        </div>
      </div>
    </Modal>
  );
}

function LinhaValor({ C, item, inventarioId, podeEditar, isMobile, onSalvo }) {
  const valorServidor = numeroParaInput(item.custo_unitario);
  const [texto, setTexto] = useState(valorServidor);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [modalNF, setModalNF] = useState(false);
  const alterado = texto !== valorServidor;

  useEffect(() => { setTexto(valorServidor); }, [valorServidor]);

  const custoDigitado = parseNumero(texto);
  const valorPrevisto = custoDigitado !== null ? Number(item.quantidade_contada) * Number(custoDigitado) : null;

  async function salvar(dados) {
    setSalvando(true);
    setErro("");
    try {
      const atualizado = await putEstoqueValorAbertura(inventarioId, item.produto_id, dados);
      onSalvo(atualizado);
    } catch (e) {
      setErro(e.message);
      throw e;
    } finally {
      setSalvando(false);
    }
  }

  function salvarManual() {
    if (texto.trim() && custoDigitado === null) { setErro("Número inválido"); return; }
    salvar({ origem_custo: "MANUAL", custo_unitario: custoDigitado }).catch(() => {});
  }

  const valorizado = item.custo_unitario !== null && item.custo_unitario !== undefined;
  return (
    <div style={{ padding: "10px 12px", borderTop: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", flexWrap: isMobile ? "wrap" : "nowrap", alignItems: "center", gap: 10 }}>
        <div style={{ flex: "1 1 180px", minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{item.produto_nome}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            {formatQtd(item.quantidade_contada)} {item.unidade_sigla}
            {item.origem_custo === "NF" && item.referencia_nf && <> · <FileText size={11} style={{ verticalAlign: "-1px" }} /> {item.referencia_nf}</>}
            {item.origem_custo === "MANUAL" && " · valor manual"}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: isMobile ? "1 1 100%" : "0 0 auto" }}>
          <NumeroInput
            C={C}
            casas={6}
            value={texto}
            onChange={(v) => { setTexto(v); setErro(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") salvarManual(); }}
            disabled={!podeEditar}
            placeholder="R$ / un."
            aria-label={`Valor unitário de ${item.produto_nome}`}
            style={{ width: 110, borderColor: alterado ? C.brand : C.border }}
          />
          <span style={{ fontSize: 13, fontWeight: 700, minWidth: 92, textAlign: "right", color: valorizado || alterado ? C.text : C.muted }}>
            {formatBRL(alterado ? valorPrevisto : item.valor_total)}
          </span>
          {podeEditar && (
            <>
              <button onClick={salvarManual} disabled={salvando || !alterado} aria-label="Salvar valor"
                style={{ width: 44, height: 44, borderRadius: 10, border: "none", cursor: alterado ? "pointer" : "default", background: alterado ? C.brand : valorizado ? C.success : C.border, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, opacity: salvando ? 0.6 : 1 }}>
                <Check size={20} />
              </button>
              <button onClick={() => setModalNF(true)} title="Calcular pela nota fiscal" style={{ ...btnStyle(C.brand, true), padding: "8px 10px", flexShrink: 0 }}>
                <FileText size={15} /> NF
              </button>
            </>
          )}
        </div>
      </div>
      {erro && <div style={{ fontSize: 12, color: C.danger, marginTop: 4 }}>{erro}</div>}
      {modalNF && (
        <ModalValorNF C={C} item={item} onClose={() => setModalNF(false)} onSalvar={async (dados) => { await salvar(dados); setModalNF(false); }} />
      )}
    </div>
  );
}

function AbaValorizacao({ C, abertura, podeEditar, isMobile, onItemSalvo }) {
  const [soPendentes, setSoPendentes] = useState(false);
  const comSaldo = abertura.itens.filter((i) => Number(i.quantidade_contada) > 0);
  const lista = soPendentes ? comSaldo.filter((i) => i.custo_unitario === null) : comSaldo;
  const { resumo } = abertura;

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 12 }}>
        <Card C={C}><BarraProgresso C={C} feitos={resumo.valorizados} total={resumo.com_saldo} rotulo="valorizados" /></Card>
        <Card C={C}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Valor total em estoque</div>
          <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>{formatBRL(resumo.valor_total)}</div>
        </Card>
      </div>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 12px" }}>
        Só produtos com quantidade maior que zero precisam de valor. Informe o valor por unidade padrão ou use <strong>NF</strong> para calcular a partir da nota mais recente.
      </p>
      <button onClick={() => setSoPendentes((v) => !v)} style={{ ...btnStyle(C.brand, !soPendentes), marginBottom: 12 }}>
        {soPendentes ? "Mostrando só sem valor" : "Só sem valor"}
      </button>
      {comSaldo.length === 0 ? (
        <Card C={C} style={{ textAlign: "center", color: C.muted, fontSize: 13 }}>Nenhum produto com quantidade contada ainda.</Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {agruparPorCategoria(lista).map(([categoria, itens]) => (
            <div key={categoria} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ padding: 12, background: C.bgActive, fontSize: 14, fontWeight: 800 }}>{categoria}</div>
              {itens.map((item) => (
                <LinhaValor key={item.produto_id} C={C} item={item} inventarioId={abertura.id} podeEditar={podeEditar} isMobile={isMobile} onSalvo={onItemSalvo} />
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Conclusão ────────────────────────────────────────────────────────────────

function AbaConclusao({ C, abertura, perm, onConcluida, onCancelada }) {
  const [confirmando, setConfirmando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const { resumo } = abertura;
  const naoContados = resumo.pendencias.nao_contados.length;
  const semValor = resumo.pendencias.sem_valor.length;
  const pronto = naoContados === 0 && semValor === 0;

  const porCategoria = agruparPorCategoria(abertura.itens).map(([categoria, itens]) => ({
    categoria,
    produtos: itens.filter((i) => Number(i.quantidade_contada) > 0).length,
    valor: itens.reduce((s, i) => s + Number(i.valor_total || 0), 0),
  }));

  async function executar(acao) {
    setSalvando(true);
    setErro("");
    try {
      await acao();
    } catch (e) {
      setErro(e.data?.mensagem ? `${e.data.mensagem}: ${e.data.nao_contados} não contado(s), ${e.data.sem_valor} sem valor.` : e.message);
    } finally {
      setSalvando(false);
      setConfirmando(false);
    }
  }

  const Checagem = ({ ok, texto }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, padding: "6px 0" }}>
      {ok ? <CheckCircle2 size={18} color={C.success} /> : <AlertTriangle size={18} color={C.warning} />}
      <span>{texto}</span>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 720 }}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <Card C={C}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>Pendências</h3>
        <Checagem ok={naoContados === 0} texto={naoContados === 0 ? "Todos os produtos foram contados" : `${naoContados} produto(s) sem contagem`} />
        <Checagem ok={semValor === 0} texto={semValor === 0 ? "Todos os produtos com saldo têm valor" : `${semValor} produto(s) com saldo e sem valor unitário`} />
      </Card>
      <Card C={C}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>Saldo de abertura em {formatData(abertura.data)}</h3>
        {porCategoria.map((c) => (
          <div key={c.categoria} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
            <span>{c.categoria} <span style={{ color: C.muted }}>· {c.produtos} produto(s)</span></span>
            <strong>{formatBRL(c.valor)}</strong>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 800, paddingTop: 10 }}>
          <span>Total</span><span>{formatBRL(resumo.valor_total)}</span>
        </div>
      </Card>
      {perm.financeiro && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => setConfirmando(true)} disabled={!pronto || salvando} style={{ ...btnStyle(C.success), opacity: pronto ? 1 : 0.5, cursor: pronto ? "pointer" : "not-allowed", flex: "1 1 220px" }}>
            <CheckCircle2 size={16} /> Concluir abertura
          </button>
          <button onClick={() => setCancelando(true)} style={{ ...btnStyle(C.danger, true), flex: "0 1 auto" }}>Cancelar vistoria</button>
        </div>
      )}
      {!perm.financeiro && <div style={{ fontSize: 12, color: C.muted }}>A conclusão é feita pelo financeiro.</div>}

      {confirmando && (
        <Modal C={C} titulo="Concluir a abertura?" onClose={() => setConfirmando(false)} largura={440}
          rodape={<>
            <button onClick={() => setConfirmando(false)} style={btnStyle(C.muted, true)}>Voltar</button>
            <button onClick={() => executar(async () => { await postEstoqueConcluirAbertura(abertura.id); onConcluida(); })} disabled={salvando} style={btnStyle(C.success)}>
              {salvando ? "Concluindo..." : "Concluir"}
            </button>
          </>}>
          <p style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>
            Serão gerados os saldos de abertura de <strong>{resumo.com_saldo} produto(s)</strong>, total de <strong>{formatBRL(resumo.valor_total)}</strong>,
            com data de {formatData(abertura.data)}. Depois de concluída, a vistoria não pode mais ser alterada — correções entram como ajuste.
          </p>
        </Modal>
      )}
      {cancelando && (
        <Modal C={C} titulo="Cancelar a vistoria?" onClose={() => setCancelando(false)} largura={440}
          rodape={<>
            <button onClick={() => setCancelando(false)} style={btnStyle(C.muted, true)}>Voltar</button>
            <button onClick={() => executar(async () => { await postEstoqueCancelarInventario(abertura.id, motivo.trim()); setCancelando(false); onCancelada(); })} disabled={salvando || !motivo.trim()} style={{ ...btnStyle(C.danger), opacity: motivo.trim() ? 1 : 0.5 }}>
              Cancelar vistoria
            </button>
          </>}>
          <p style={{ fontSize: 13, color: C.muted, marginTop: 0 }}>As contagens ficam registradas, mas a vistoria deixa de valer e uma nova pode ser iniciada.</p>
          <Campo label="Motivo" C={C} obrigatorio>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} style={inputStyle(C)} autoFocus />
          </Campo>
        </Modal>
      )}
    </div>
  );
}

// ─── Concluída (somente leitura) ──────────────────────────────────────────────

function AberturaConcluida({ C, abertura, isMobile }) {
  const navigate = useNavigate();
  const comSaldo = abertura.itens.filter((i) => Number(i.quantidade_contada) > 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Card C={C} style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 800, color: C.success }}>
            <CheckCircle2 size={20} /> Abertura concluída
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
            Saldo de {formatData(abertura.data)} · concluída {abertura.concluido_por_nome ? `por ${abertura.concluido_por_nome} ` : ""}em {formatData(abertura.concluido_em)}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Valor de abertura</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{formatBRL(abertura.resumo.valor_total)}</div>
        </div>
        <button onClick={() => navigate("/estoque/posicao")} style={btnStyle(C.brand)}>Ver posição de estoque</button>
      </Card>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ color: C.muted, fontSize: 11, textTransform: "uppercase" }}>
              {["Produto", "Qtd.", ...(isMobile ? [] : ["Valor unit.", "Origem"]), "Valor"].map((h, idx) => (
                <th key={h} style={{ padding: "10px 12px", textAlign: idx === 0 || h === "Origem" ? "left" : "right", borderBottom: `1px solid ${C.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comSaldo.map((i) => (
              <tr key={i.produto_id}>
                <td style={{ padding: "8px 12px", borderBottom: `1px solid ${C.border}` }}>{i.produto_nome}<div style={{ fontSize: 11, color: C.muted }}>{i.categoria_nome}</div></td>
                <td style={{ padding: "8px 12px", borderBottom: `1px solid ${C.border}`, textAlign: "right", whiteSpace: "nowrap" }}>{formatQtd(i.quantidade_contada)} {i.unidade_sigla}</td>
                {!isMobile && <td style={{ padding: "8px 12px", borderBottom: `1px solid ${C.border}`, textAlign: "right" }}>{formatBRL(i.custo_unitario)}</td>}
                {!isMobile && <td style={{ padding: "8px 12px", borderBottom: `1px solid ${C.border}`, color: C.muted }}>{i.origem_custo === "NF" ? i.referencia_nf : "Manual"}</td>}
                <td style={{ padding: "8px 12px", borderBottom: `1px solid ${C.border}`, textAlign: "right", fontWeight: 700 }}>{formatBRL(i.valor_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── View ─────────────────────────────────────────────────────────────────────

export default function VistoriaAberturaView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const perm = permissoesEstoque(getRoleFromToken());
  const [estado, setEstado] = useState(null); // { abertura, estoque_tem_movimentacao }
  const [aba, setAba] = useState("contagem");
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    setErro("");
    try {
      setEstado(await getEstoqueAbertura());
    } catch (e) {
      setErro(e.message);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Atualiza só o item salvo + o resumo, sem recarregar a lista (não perde o que está digitado nas outras linhas).
  const onItemSalvo = useCallback((itemAtualizado) => {
    setEstado((atual) => {
      if (!atual?.abertura) return atual;
      const itens = atual.abertura.itens.map((i) => (i.produto_id === itemAtualizado.produto_id ? itemAtualizado : i));
      const temSaldo = (i) => Number(i.quantidade_contada) > 0;
      const resumo = {
        ...atual.abertura.resumo,
        contados: itens.filter((i) => i.quantidade_contada !== null).length,
        com_saldo: itens.filter(temSaldo).length,
        valorizados: itens.filter((i) => temSaldo(i) && i.custo_unitario !== null).length,
        valor_total: itens.reduce((s, i) => s + Number(i.valor_total || 0), 0),
        pendencias: {
          nao_contados: itens.filter((i) => i.quantidade_contada === null).map((i) => i.produto_id),
          sem_valor: itens.filter((i) => temSaldo(i) && i.custo_unitario === null).map((i) => i.produto_id),
        },
      };
      return { ...atual, abertura: { ...atual.abertura, itens, resumo } };
    });
  }, []);

  const abertura = estado?.abertura;
  const emAndamento = abertura?.status === "ABERTO";
  const abas = [
    { id: "contagem", label: "1. Contagem" },
    ...(perm.financeiro ? [{ id: "valorizacao", label: "2. Valorização" }, { id: "conclusao", label: "3. Conclusão" }] : []),
  ];

  return (
    <EstoqueLayout
      titulo="Vistoria de Abertura"
      isLight={isLight}
      setIsLight={setIsLight}
      acoesHeader={emAndamento && (
        <button onClick={carregar} aria-label="Atualizar" title="Atualizar (ver contagens de outras pessoas)" style={{ display: "flex", alignItems: "center", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: 8, minHeight: 38, color: "#fff", cursor: "pointer" }}>
          <RefreshCw size={14} />
        </button>
      )}
    >
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      {!estado ? (
        !erro && <div style={{ color: C.muted, fontSize: 13, padding: 24, textAlign: "center" }}>Carregando...</div>
      ) : !abertura ? (
        <IniciarVistoria C={C} perm={perm} estoqueTemMovimentacao={estado.estoque_tem_movimentacao} onIniciada={carregar} />
      ) : !emAndamento ? (
        <AberturaConcluida C={C} abertura={abertura} isMobile={isMobile} />
      ) : (
        <>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
            Vistoria de {formatData(abertura.data)}{abertura.criado_por_nome ? ` · iniciada por ${abertura.criado_por_nome}` : ""}
          </div>
          {abas.length > 1 && (
            <div style={{ display: "flex", gap: 4, marginBottom: 12, overflowX: "auto", borderBottom: `1px solid ${C.border}` }}>
              {abas.map((a) => (
                <button key={a.id} onClick={() => setAba(a.id)} style={{ background: "none", border: "none", borderBottom: `2px solid ${aba === a.id ? C.brand : "transparent"}`, color: aba === a.id ? C.brand : C.muted, fontWeight: aba === a.id ? 700 : 500, fontSize: 14, padding: "10px 12px", minHeight: 44, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                  {a.label}
                </button>
              ))}
            </div>
          )}
          <div style={{ maxWidth: 820 }}>
            {aba === "contagem" && <AbaContagem C={C} inventario={abertura} perm={perm} podeEditar onItemSalvo={onItemSalvo} onProdutoCriado={carregar} />}
            {aba === "valorizacao" && <AbaValorizacao C={C} abertura={abertura} podeEditar={perm.financeiro} isMobile={isMobile} onItemSalvo={onItemSalvo} />}
            {aba === "conclusao" && <AbaConclusao C={C} abertura={abertura} perm={perm} onConcluida={carregar} onCancelada={() => { setAba("contagem"); carregar(); }} />}
          </div>
        </>
      )}
    </EstoqueLayout>
  );
}
